/**
 * The customer book.
 *
 * WHAT IT IS FOR
 * --------------
 * A shop that has served a thousand people knows a thousand things it has
 * never been able to look at: who comes back, who used to and stopped, and who
 * is worth ringing when there is an offer on.
 *
 * The counts here are worked out from the orders themselves rather than read
 * off a running total kept on each customer, so a void or a refund cannot
 * leave this page saying something the orders behind it do not.
 *
 * WHY THE EXPORT IS A LINK AND NOT A BUTTON
 * -----------------------------------------
 * The file is built by the server, which is where the permission is checked.
 * A button that assembled the CSV in the browser would need every customer
 * loaded into the page first — the whole book on screen to save a copy of the
 * whole book.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../../lib/api.js';
import { formatPKR } from '@foodrun/shared';

const PAGE = 50;

/** A figure with nothing behind it yet reads better as a dash than as zero. */
function orNothing(value, render) {
  return value ? render(value) : <span className="text-ink-3">—</span>;
}

function Card({ title, children }) {
  return (
    <section className="rounded-xl border border-rule bg-surface-2 p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

/** A short table of people, used for both "most orders" and "most spent". */
function Leaderboard({ rows, measure }) {
  if (!rows?.length) {
    return <p className="text-sm text-ink-3">Nobody has ordered twice yet.</p>;
  }

  return (
    <ol className="space-y-2">
      {rows.map((customer, index) => (
        <li key={customer.id} className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-sm text-ink">
            <span className="mr-2 text-xs tabular-nums text-ink-3">{index + 1}</span>
            {customer.name}
            <span className="ml-2 text-xs text-ink-3">{customer.phone}</span>
          </span>
          <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">
            {measure === 'orders'
              ? `${customer.orders} order${customer.orders === 1 ? '' : 's'}`
              : formatPKR(customer.spent)}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function CustomersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const summary = useQuery({
    queryKey: ['crm', 'summary'],
    queryFn: () => api.get('/crm/summary'),
  });

  const book = useQuery({
    queryKey: ['crm', 'customers', search, page],
    queryFn: () =>
      api.get(withQuery('/crm/customers', { q: search || undefined, limit: PAGE, offset: page * PAGE })),
    // A page of customers does not change between one keystroke and the next.
    placeholderData: (previous) => previous,
  });

  const customers = book.data?.customers ?? [];
  const total = book.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE) - 1);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-lg font-semibold text-ink">Customers</h1>

        {/*
          A plain link, so the browser downloads it the way it downloads
          anything else — and so the server, which is where the permission
          lives, is the thing that decides whether it may be had.
        */}
        <a
          href="/api/crm/customers/export"
          className="rounded-lg border border-rule px-3 py-2 text-sm text-ink hover:bg-surface-3"
        >
          Export CSV
        </a>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <Card title="How many we know">
          <p className="text-3xl font-semibold tabular-nums text-ink">
            {summary.data?.customers ?? '—'}
          </p>
          <p className="mt-1 text-xs text-ink-3">
            {summary.data?.orderedBefore ?? 0} have ordered at least once
          </p>
        </Card>

        <Card title="Orders most often">
          <Leaderboard rows={summary.data?.mostOrders} measure="orders" />
        </Card>

        <Card title="Spends the most">
          <Leaderboard rows={summary.data?.mostSpent} measure="spent" />
        </Card>
      </div>

      <div className="rounded-xl border border-rule bg-surface-2">
        <div className="border-b border-rule p-3">
          <label className="block">
            <span className="sr-only">Search customers</span>
            <input
              aria-label="Search customers"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by name or number"
              className="h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>
        </div>

        {book.isLoading ? (
          <p className="p-8 text-center text-sm text-ink-3">Looking…</p>
        ) : customers.length === 0 ? (
          <p className="p-8 text-center text-sm text-ink-3">
            {search
              ? 'Nobody by that name or number.'
              : 'No customers yet. They are kept automatically when somebody gives a phone number.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-ink-3">
                <tr className="border-b border-rule">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium">Address</th>
                  <th className="px-3 py-2 text-right font-medium">Orders</th>
                  <th className="px-3 py-2 text-right font-medium">Spent</th>
                  <th className="px-3 py-2 font-medium">Last order</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-b border-rule/60 last:border-0">
                    <td className="px-3 py-2 text-ink">{customer.name}</td>
                    <td className="px-3 py-2 tabular-nums text-ink-2">{customer.phone}</td>
                    <td className="max-w-[22rem] truncate px-3 py-2 text-ink-3">
                      {orNothing(customer.address, (a) => a)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {customer.total_orders}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {formatPKR(customer.total_spent ?? 0)}
                    </td>
                    <td className="px-3 py-2 text-ink-3">
                      {orNothing(customer.last_order_date, (d) => String(d).slice(0, 10))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE && (
          <div className="flex items-center justify-between gap-3 border-t border-rule p-3">
            <span className="text-xs text-ink-3">
              {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total}
            </span>
            <span className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-lg border border-rule px-3 py-1.5 text-sm text-ink disabled:opacity-40 hover:enabled:bg-surface-3"
              >
                Back
              </button>
              <button
                type="button"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                className="rounded-lg border border-rule px-3 py-1.5 text-sm text-ink disabled:opacity-40 hover:enabled:bg-surface-3"
              >
                Next
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
